import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Input, Switch, Table, message, Space, Modal, InputNumber, Alert, Upload, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, MailOutlined, SendOutlined, CheckCircleOutlined, UploadOutlined, FileExcelOutlined, InfoCircleOutlined, EditOutlined, BellOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';

const API_URL = '/api/notifications';
const IMPORT_API_URL = '/api/import';

function Settings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [form] = Form.useForm();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      message.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const checkSmtpStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/verify-smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmtpStatus(response.data.success ? 'success' : 'error');
    } catch (error) {
      setSmtpStatus('error');
    }
  };

  useEffect(() => {
    fetchSettings();
    checkSmtpStatus();
  }, []);

  const handleAdd = () => {
    form.resetFields();
    setEditingRecord(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue(record);
    setEditingRecord(record);
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      if (editingRecord) {
        await axios.put(`${API_URL}/${editingRecord.id}`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Настройка обновлена');
      } else {
        await axios.post(API_URL, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Настройка добавлена');
      }
      setModalVisible(false);
      fetchSettings();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Ошибка сохранения';
      message.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Настройка удалена');
      fetchSettings();
    } catch (error) {
      message.error('Ошибка удаления');
    }
  };

  const handleToggle = async (record) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/${record.id}`, {
        is_active: !record.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Статус изменен');
      fetchSettings();
    } catch (error) {
      message.error('Ошибка изменения статуса');
    }
  };

  const handleTestEmail = async (email) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/test`, { email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Тестовое письмо отправлено');
    } catch (error) {
      message.error('Ошибка отправки тестового письма');
    }
  };

  const handleSendNotifications = async () => {
    setSendingNotifications(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success(response.data.message || 'Уведомления отправлены');
    } catch (error) {
      message.error('Ошибка отправки уведомлений');
    } finally {
      setSendingNotifications(false);
    }
  };

  const handleClearLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success(response.data.message || 'Логи очищены');
    } catch (error) {
      message.error('Ошибка очистки логов');
    }
  };

  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileUpload = async (file) => {
    setUploading(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${IMPORT_API_URL}/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.success) {
        message.success(response.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Ошибка загрузки файла';
      message.error(errorMsg);
    } finally {
      setUploading(false);
    }
    
    return false; // Предотвращаем автоматическую загрузку
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'За сколько дней',
      key: 'days',
      render: (record) => (
        <span>
          {record.days_before} дн.
          {record.days_before_secondary && (
            <span style={{ color: '#fa8c16', marginLeft: 8 }}>
              + {record.days_before_secondary} дн.
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Активно',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          onChange={() => handleToggle(record)}
        />
      )
    },
    {
      title: 'Последняя отправка',
      dataIndex: 'last_sent',
      key: 'last_sent',
      render: (date) => date ? new Date(date).toLocaleString('ru-RU') : 'Никогда',
    },
    {
      title: 'Действия',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => handleTestEmail(record.email)}
          >
            Тест
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Редактировать
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Удалить
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="app">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><MailOutlined /> Настройки уведомлений</span>
            <Space>
              <Button 
                icon={<ClearOutlined />} 
                onClick={handleClearLogs}
              >
                Очистить логи
              </Button>
              <Button 
                type="primary" 
                icon={<BellOutlined />} 
                onClick={handleSendNotifications}
                loading={sendingNotifications}
              >
                Отправить уведомления
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Добавить email
              </Button>
            </Space>
          </div>
        }
      >
        {smtpStatus === 'success' ? (
          <Alert
            message="SMTP подключение работает"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        ) : smtpStatus === 'error' ? (
          <Alert
            message="SMTP не настроен. Проверьте настройки в .env файле на сервере"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <p style={{ marginBottom: 16, color: '#666' }}>
          Здесь можно настроить email-адреса для получения уведомлений об истекающих патентах. 
          Уведомления отправляются автоматически каждый день в 8:00.
        </p>

        <Table
          columns={columns}
          dataSource={settings}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Card
        title={<span><FileExcelOutlined /> Импорт сотрудников из Excel</span>}
        style={{ marginTop: 24 }}
      >
        <Alert
          message="Требования к файлу Excel"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Первая строка должна содержать заголовки колонок</li>
              <li>Обязательная колонка: ФИО (первая колонка)</li>
              <li>Опциональные колонки: Дата рождения, Дата оплаты, Дата регистрации, Дата мед страховки, Дата мед полиса, Профессия, Дата выдачи патента, Срок действия патента, ИНН, Статус</li>
              <li>Форматы дат: DD.MM.YYYY или DD.MM.YY</li>
              <li>Поддерживаемые форматы: .xlsx, .xls</li>
            </ul>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Upload
          beforeUpload={handleFileUpload}
          accept=".xlsx,.xls"
          showUploadList={false}
        >
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            type="primary"
          >
            {uploading ? 'Загрузка...' : 'Выбрать файл Excel'}
          </Button>
        </Upload>

        {importResult && (
          <div style={{ marginTop: 16 }}>
            <Alert
              message={importResult.message}
              type={importResult.errors > 0 ? 'warning' : 'success'}
              showIcon
            />
            {importResult.errorDetails && importResult.errorDetails.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary">Ошибки в строках:</Typography.Text>
                <ul style={{ margin: '8px 0', paddingLeft: 20, color: '#ff4d4f' }}>
                  {importResult.errorDetails.slice(0, 5).map((err, idx) => (
                    <li key={idx}>Строка {err.row}: {err.error}</li>
                  ))}
                  {importResult.errorDetails.length > 5 && (
                    <li>...и еще {importResult.errorDetails.length - 5} ошибок</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <Modal
        title={editingRecord ? 'Редактировать уведомление' : 'Добавить уведомление'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="email"
            label="Email адрес"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Некорректный email' }
            ]}
          >
            <Input placeholder="example@mail.ru" />
          </Form.Item>

          <Form.Item
            name="days_before"
            label="Первое уведомление (за сколько дней)"
            initialValue={30}
            rules={[
              { required: true, message: 'Укажите количество дней' },
              { type: 'number', min: 1, max: 365, message: 'От 1 до 365 дней' }
            ]}
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              placeholder="30"
            />
          </Form.Item>

          <Form.Item
            name="days_before_secondary"
            label="Повторное уведомление (за сколько дней, опционально)"
            initialValue={10}
            rules={[
              { type: 'number', min: 1, max: 365, message: 'От 1 до 365 дней' }
            ]}
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              placeholder="10 (оставьте пустым чтобы отключить)"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Активно" unCheckedChildren="Выключено" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Отмена</Button>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Settings;
